import { BrowserRouter, Routes, Route, useNavigate, Link } from 'react-router-dom';
import { AuthProvider, BrandingProvider, useAuth, useBranding } from '@context';
import { LoginPage, HomePage, CoursePage, LearningPage, QuestionPracticePage, FlashcardsPage, ReviewContentPage } from '@pages';
import { AdminLayout } from './pages/admin/AdminLayout';
import { CoursesPage } from './pages/admin/CoursesPage';
import { CourseDetailPage } from './pages/admin/CourseDetailPage';
import { QuestionsPage } from './pages/admin/QuestionsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { BrandingPage } from './pages/admin/BrandingPage';
import { EmailsPage } from './pages/admin/EmailsPage';
import { EmailLogsPage } from './pages/admin/EmailLogsPage';
import { EmailConfigurationPage } from './pages/admin/EmailConfigurationPage';
import { ROUTES } from '@config/routes';
import { Navbar, Container, Nav, Button } from 'react-bootstrap';
import { RequireAuth } from '@components/common/RequireAuth';
import { RequireAdmin } from '@components/common/RequireAdmin';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@styles/main.css';

/**
 * Main App Component
 */

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="app">
            <AppNavbar />
            <main className="main-content">
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path={ROUTES.HOME} element={<RequireAuth><HomePage /></RequireAuth>} />
                <Route path={ROUTES.COURSE_OVERVIEW} element={<RequireAuth><CoursePage /></RequireAuth>} />
                <Route path={ROUTES.LEARNING_SESSION} element={<RequireAuth><LearningPage /></RequireAuth>} />
                <Route path={ROUTES.QUESTION_PRACTICE} element={<RequireAuth><QuestionPracticePage /></RequireAuth>} />
                <Route path={ROUTES.FLASHCARDS} element={<RequireAuth><FlashcardsPage /></RequireAuth>} />
                <Route path={ROUTES.REVIEW_CONTENT} element={<RequireAuth><ReviewContentPage /></RequireAuth>} />
                {/* Admin routes */}
                <Route path={ROUTES.ADMIN_ROOT} element={<RequireAuth><RequireAdmin><AdminLayout /></RequireAdmin></RequireAuth>}>
                  <Route path={ROUTES.ADMIN_COURSES.replace('/admin/', '')} element={<CoursesPage />} />
                  <Route path={ROUTES.ADMIN_COURSE_DETAIL.replace('/admin/', '')} element={<CourseDetailPage />} />
                  <Route path={ROUTES.ADMIN_QUESTIONS.replace('/admin/', '')} element={<QuestionsPage />} />
                  <Route path={ROUTES.ADMIN_USERS.replace('/admin/', '')} element={<UsersPage />} />
                  <Route
                    path={ROUTES.ADMIN_BRANDING.replace('/admin/', '')}
                    element={<RequireAdmin adminOnly><BrandingPage /></RequireAdmin>}
                  />
                  <Route
                    path={ROUTES.ADMIN_EMAILS.replace('/admin/', '')}
                    element={<RequireAdmin adminOnly><EmailsPage /></RequireAdmin>}
                  />
                  <Route
                    path={ROUTES.ADMIN_EMAIL_LOGS.replace('/admin/', '')}
                    element={<RequireAdmin adminOnly><EmailLogsPage /></RequireAdmin>}
                  />
                  <Route
                    path={ROUTES.ADMIN_EMAIL_CONFIG.replace('/admin/', '')}
                    element={<RequireAdmin adminOnly><EmailConfigurationPage /></RequireAdmin>}
                  />
                </Route>
              </Routes>
            </main>
            <AppFooter />
          </div>
        </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}

/**
 * App Navbar Component
 */
function AppNavbar() {
  const { user, logout, loading } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  return (
    <Navbar bg="primary" variant="dark" expand="lg" className="mb-0">
      <Container>
        <Navbar.Brand as={Link} to={ROUTES.HOME} className="d-flex align-items-center gap-2">
          {branding?.logoUrl && (
            <img
              src={branding.logoUrl}
              alt="App logo"
              style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
            />
          )}
          <strong>{branding?.appName || 'Adaptive Learning'}</strong>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto">
            {user && <Nav.Link as={Link} to={ROUTES.HOME}>Home</Nav.Link>}
            {(user?.role === 'admin' || user?.role === 'course_manager') && (
              <Nav.Link as={Link} to={ROUTES.ADMIN_COURSES}>Admin</Nav.Link>
            )}
            {!user && !loading ? (
              <Button 
                variant="outline-light" 
                size="sm" 
                onClick={() => navigate('/login')}
                className="ms-2"
              >
                Login
              </Button>
            ) : user ? (
              <>
                <Nav.Link disabled>👤 {user.name}</Nav.Link>
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  onClick={() => {
                    logout();
                    navigate('/login');
                  }}
                  className="ms-2"
                >
                  Logout
                </Button>
              </>
            ) : null}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

/**
 * App Footer Component
 */
function AppFooter() {
  return (
    <footer className="app-footer bg-light py-4 mt-5">
      <Container>
        <div className="text-center text-muted">
          <p className="mb-0">
            © 2026 Soundings Academy | Managed by Wayfinding Technology Group LLC
          </p>
        </div>
      </Container>
    </footer>
  );
}

export default App;
